import React, { useEffect, useId } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useIsWebView, useToken } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { appBridge } from '../../../shared/services/bridge';
import { RECEIPT_ISSUE_TEXTS } from '../constants/receiptTexts';
import { usePaymentInfo } from '../hooks/usePaymentInfo';
import { useReceiptIssue } from '../hooks/useReceiptIssue';
import PaymentSummarySection from './PaymentSummarySection';

import './ReceiptIssue.css';
import './PaymentConfirm.css';
import './PaymentCommon.css';

/**
 * 領収書発行画面（課題表381）
 *
 * クレジットカード・現金の支払完了後に表示する。
 * ドクター側の決済完了画面（カルテルート現金）と同じ構成:
 * 支払内訳 → 宛名入力（任意）→ 「領収書を発行する」。
 *
 * - 宛名を入力して発行: POST /api/v1/inquiries/receipt → PDF をアプリ内ビューアで表示 → 発行済み表示
 * - 領収書は不要: 発行せずに決済完了画面（/payment-complete）へ進む
 * - `paymentCompleted` はこの画面では送らない（決済完了画面が送り、Flutter フッターにアンケート導線が出る）
 */
const ReceiptIssue: React.FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = useToken();
  const isWebView = useIsWebView();
  const inputId = useId();

  const { data: paymentData, isLoading, error: paymentError } = usePaymentInfo(id, token);
  const contentRef = useWebViewLoading(isLoading);

  const {
    status,
    addressName,
    setAddressName,
    canSubmit,
    errorMessage,
    submit,
    openReceipt,
  } = useReceiptIssue(id, token);

  // 戻るで待機画面・決済確認画面に戻れないようにする（二重決済・状態不整合の防止）
  useEffect(() => {
    if (isWebView) {
      appBridge.hideBackButton();
    }
  }, [isWebView]);

  // 金額情報を計算（CashPaymentConfirm と同じ式: 事務手数料加算後に消費税）
  // ※ 早期 return より前で計算しているのは Hooks の呼び出し順を固定するため（値は下の分岐で使う）
  const price = paymentData?.price ?? 0;
  const taxRate = paymentData?.taxRate ?? 0;
  const commissionRate = paymentData?.commissionRate ?? 0;
  const commissionAmount = Math.floor((price * commissionRate) / 100);
  const subtotalWithCommission = price + commissionAmount;
  const taxAmount = Math.floor((subtotalWithCommission * taxRate) / 100);
  const totalAmount = subtotalWithCommission + taxAmount;

  // Web 直接アクセスで token をクエリで持ち回っている場合は遷移先にも引き継ぐ
  // （PaymentComplete と同じ運用。エンコードして特殊文字でクエリが壊れないようにする）
  const tokenParam = searchParams.get('token');
  const completeQuery = tokenParam ? `?${new URLSearchParams({ token: tokenParam })}` : '';
  const handleProceed = () => {
    navigate(`/inquiries/${id}/payment-complete${completeQuery}`, { replace: true });
  };

  const isSubmitting = status === 'submitting';
  const isIssued = status === 'issued';

  // ローディング中は Flutter 側のローディングが表示されるため null を返す（CashPaymentConfirm と同じ）
  if (isLoading) {
    return null;
  }

  // 支払情報を取得できない場合は内訳を出せないため発行させない。
  // 「領収書は不要」で決済完了画面へは進める（アンケート導線を塞がない）
  if (paymentError || !paymentData) {
    return (
      <div ref={contentRef} className="confirm-container receipt-issue">
        <main className="confirm-content">
          <p role="alert">{RECEIPT_ISSUE_TEXTS.LOAD_ERROR}</p>
        </main>
        <footer className="confirm-footer">
          <button type="button" className="button-secondary" onClick={handleProceed}>
            {RECEIPT_ISSUE_TEXTS.SKIP}
          </button>
        </footer>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="confirm-container receipt-issue">
      <main className="confirm-content">
        {isIssued ? (
          <section className="receipt-issue-done" aria-live="polite">
            <img src="/assets/images/check-circle.png" alt="完了" className="receipt-issue-icon" />
            <h2 className="content-title">{RECEIPT_ISSUE_TEXTS.ISSUED_TITLE}</h2>
            <p className="receipt-issue-description">{RECEIPT_ISSUE_TEXTS.ISSUED_DESCRIPTION}</p>
          </section>
        ) : (
          <>
            <h2 className="content-title">{RECEIPT_ISSUE_TEXTS.TITLE}</h2>
            <p className="receipt-issue-description">{RECEIPT_ISSUE_TEXTS.DESCRIPTION}</p>

            <PaymentSummarySection
              estimateGroups={paymentData?.estimateGroups}
              subtotalExcludingCommission={price}
              commissionAmount={commissionAmount}
              taxAmount={taxAmount}
              totalAmount={totalAmount}
            />

            <div className="form-field">
              <label htmlFor={inputId} className="form-label receipt-issue-address-label">
                {RECEIPT_ISSUE_TEXTS.ADDRESS_LABEL}
                <span className="receipt-issue-address-note">{RECEIPT_ISSUE_TEXTS.ADDRESS_NOTE}</span>
              </label>
              <input
                id={inputId}
                type="text"
                className="form-input"
                value={addressName}
                placeholder={RECEIPT_ISSUE_TEXTS.ADDRESS_PLACEHOLDER}
                autoComplete="name"
                disabled={isSubmitting}
                onChange={(e) => setAddressName(e.target.value)}
              />
            </div>

            {errorMessage && (
              <p className="error-text" role="alert">
                {errorMessage}
              </p>
            )}
          </>
        )}
      </main>

      <footer className="confirm-footer">
        {isIssued ? (
          <>
            <button type="button" className="button-primary" onClick={openReceipt}>
              {RECEIPT_ISSUE_TEXTS.VIEW}
            </button>
            <button type="button" className="button-secondary" onClick={handleProceed}>
              {RECEIPT_ISSUE_TEXTS.PROCEED}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="button-primary"
              disabled={!canSubmit}
              onClick={submit}
            >
              {isSubmitting ? RECEIPT_ISSUE_TEXTS.SUBMITTING : RECEIPT_ISSUE_TEXTS.SUBMIT}
            </button>
            <button
              type="button"
              className="button-secondary"
              disabled={isSubmitting}
              onClick={handleProceed}
            >
              {RECEIPT_ISSUE_TEXTS.SKIP}
            </button>
          </>
        )}
      </footer>
    </div>
  );
};

export default ReceiptIssue;
